import * as React from 'react'
import Navbar from '../setup/Navbar'
import { useDispatch, useSelector } from 'react-redux'
import { getTodayClosingData, createClosing, getClosingHistory, getEstimatedStockPreview } from '../../features/closing/closingSlice'
import { getAllUsers } from '../../features/auth/authSlice'
import { Container, Typography, Grid2, TextField, Alert, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Autocomplete, Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Stack, IconButton, Tooltip, Chip } from '@mui/material'
import { Print as PrintIcon, Download as DownloadIcon, Visibility as VisibilityIcon } from '@mui/icons-material'
import Loading from '../../BuiltInComponent/Loading'
import HeadingText from '../../BuiltInComponent/HeadingTest'
import ButtonNormal from '../../BuiltInComponent/ButtonNormal'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { Document, Page, Text, View } from '@react-pdf/renderer'
import { pdf } from '@react-pdf/renderer'
import axios from 'axios'

export default function DailyClosing() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user, allUsers } = useSelector(state => state.auth)
  const { todayData, history, estimatedStock, loading, success, error } = useSelector(state => state.closing)

  const [collectedAmount, setCollectedAmount] = React.useState(0)
  const [openingID, setOpeningID] = React.useState(null)
  const [showVariance, setShowVariance] = React.useState(false)
  const [varianceData, setVarianceData] = React.useState(null)
  const [draftDialogOpen, setDraftDialogOpen] = React.useState(false)
  const [draftDialogData, setDraftDialogData] = React.useState(null)
  const [draftLoading, setDraftLoading] = React.useState(false)
  const [closingUserID, setClosingUserID] = React.useState(user?.userID || '')
  const closingUserOptions = React.useMemo(() => {
    return Array.isArray(allUsers) ? allUsers : []
  }, [allUsers])

  const selectedClosingUser = closingUserOptions.find((c) => Number(c.userID) === Number(closingUserID)) || null

  const [dailyClosingData, setDailyClosingData] = React.useState({
    denomination_5000: 0,
    denomination_1000: 0,
    denomination_500: 0,
    denomination_100: 0,
    denomination_50: 0,
    denomination_20: 0,
    denomination_10: 0,
    denomination_5_coin: 0,
    denomination_2_coin: 0,
    denomination_1_coin: 0,
    remarks: ''
  })

  // Fetch all users and closing data on component mount
  React.useEffect(() => {
    const userBranch = user?.branchID || user?.defaultBranchID || 1
    dispatch(getAllUsers({ cmpID: user?.cmpID, branchID: userBranch }))
    if (user?.userID) {
      setClosingUserID(user.userID)
    }
  }, [dispatch, user?.cmpID, user?.branchID, user?.defaultBranchID, user?.userID])

  // Fetch closing data when selected user changes
  React.useEffect(() => {
    if (closingUserID && user?.userID) {
      const payload = { userID: closingUserID, loggedInUserID: user.userID }
      dispatch(getTodayClosingData(payload))
      dispatch(getClosingHistory(payload))
      dispatch(getEstimatedStockPreview(payload))
    }
  }, [dispatch, closingUserID, user?.userID])
  // Update opening ID and initialize closing data when todayData is fetched
  React.useEffect(() => {
    if (todayData && todayData.openingID) {
      setOpeningID(todayData.openingID)
    }
  }, [todayData])

  // Calculate total collected amount from denominations
  React.useEffect(() => {
    const total = (parseInt(dailyClosingData.denomination_5000) || 0) * 5000 +
      (parseInt(dailyClosingData.denomination_1000) || 0) * 1000 +
      (parseInt(dailyClosingData.denomination_500) || 0) * 500 +
      (parseInt(dailyClosingData.denomination_100) || 0) * 100 +
      (parseInt(dailyClosingData.denomination_50) || 0) * 50 +
      (parseInt(dailyClosingData.denomination_20) || 0) * 20 +
      (parseInt(dailyClosingData.denomination_10) || 0) * 10 +
      (parseInt(dailyClosingData.denomination_5_coin) || 0) * 5 +
      (parseInt(dailyClosingData.denomination_2_coin) || 0) * 2 +
      (parseInt(dailyClosingData.denomination_1_coin) || 0) * 1

    setCollectedAmount(total)
  }, [dailyClosingData])

  const handleChange = (e) => {
    const { name, value } = e.target
    setDailyClosingData(prev => ({
      ...prev,
      [name]: name === 'remarks' ? value : parseInt(value) || 0
    }))
  }

  const calculateVariance = () => {
    if (!todayData) {
      alert('No opening data found. Please open the till first.')
      return
    }

    // Calculate expected cash: Opening Amount + Cash Sales only (excluding card, wallet, etc.)
    const openingAmount = parseFloat(todayData.openingAmount) || 0
    const cashSalesFromOrders = todayData.orderTypeBreakdown 
      ? todayData.orderTypeBreakdown
          .filter(item => item.paymentMethod === 'cash')
          .reduce((sum, item) => sum + parseFloat(item.amount), 0)
      : 0
    
    const expectedCash = openingAmount + cashSalesFromOrders
    const collected = parseFloat(collectedAmount) || 0
    const variance = collected - expectedCash
    const variancePercentage = expectedCash > 0 ? (variance / expectedCash) * 100 : 0

    setVarianceData({
      expectedAmount: expectedCash,
      openingAmount: openingAmount,
      cashSales: cashSalesFromOrders,
      collectedAmount: collected,
      variance: variance,
      variancePercentage: variancePercentage.toFixed(2),
      isShort: variance < 0,
      isExcess: variance > 0
    })
    setShowVariance(true)
  }

  const handlePrint = () => {
    window.print()
  }

  const handleDownloadPDF = async (reportData = todayData, reportVariance = varianceData) => {
    const selectedUser = allUsers?.find(u => Number(u.userID) === Number(reportData?.userID))
    
    const ClosingPDF = () => (
      <Document>
        <Page size="A4" style={{ padding: 40 }}>
          <View style={{ marginBottom: 20 }}>
            <Text style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 10 }}>Daily Closing Report</Text>
          </View>

          {/* User Information */}
          <View style={{ marginBottom: 20, padding: 10, backgroundColor: '#e8e8e8', borderRadius: 4 }}>
            <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ fontSize: 11, fontWeight: 'bold' }}>User:</Text>
              <Text style={{ fontSize: 11 }}>{selectedUser?.userName || `User ${reportData?.userID}`}</Text>
            </View>
            <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ fontSize: 10 }}>User ID:</Text>
              <Text style={{ fontSize: 10 }}>{reportData?.userID}</Text>
            </View>
            <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ fontSize: 10 }}>Till ID:</Text>
              <Text style={{ fontSize: 10 }}>{reportData?.tillID}</Text>
            </View>
            <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <Text style={{ fontSize: 10 }}>Opening Date & Time:</Text>
              <Text style={{ fontSize: 10 }}>{dayjs(reportData?.openingDate).format('DD-MMM-YYYY')} @ {reportData?.openingTime}</Text>
            </View>
          </View>

          <View style={{ marginBottom: 20, borderBottomWidth: 1, paddingBottom: 10 }}>
            <Text style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8 }}>Summary</Text>
            <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ fontSize: 10 }}>Opening Amount:</Text>
              <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Rs. {reportData?.openingAmount}</Text>
            </View>
            <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ fontSize: 10 }}>Total Sales:</Text>
              <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Rs. {reportData?.salesAmount}</Text>
            </View>
            <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ fontSize: 10 }}>Expected Amount:</Text>
              <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Rs. {reportData?.expectedAmount}</Text>
            </View>
            <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ fontSize: 10 }}>Cancelled Orders:</Text>
              <Text style={{ fontSize: 10, fontWeight: 'bold' }}>
                {reportData?.cancelledSummary?.count || 0} (Rs. {parseFloat(reportData?.cancelledSummary?.amount || 0).toFixed(2)})
              </Text>
            </View>
            <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ fontSize: 10 }}>Draft Orders (Summary Only):</Text>
              <Text style={{ fontSize: 10, fontWeight: 'bold' }}>
                {reportData?.draftSummary?.count || 0} (Rs. {parseFloat(reportData?.draftSummary?.amount || 0).toFixed(2)})
              </Text>
            </View>
          </View>

          {(reportData?.draftOrders || []).length > 0 && (
            <View style={{ marginBottom: 20 }}>
              <Text style={{ fontSize: 11, fontWeight: 'bold', marginBottom: 8 }}>Draft Orders Detail</Text>
              {(reportData.draftOrders || []).map((order, orderIndex) => (
                <View key={orderIndex} style={{ marginBottom: 8, borderBottomWidth: 1, paddingBottom: 5 }}>
                  <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
                    <Text style={{ fontSize: 9, fontWeight: 'bold' }}>Order #{order.orderID} ({order.orderType || '-'})</Text>
                    <Text style={{ fontSize: 9, fontWeight: 'bold' }}>Rs. {Number(order.grandTotal || 0).toFixed(2)}</Text>
                  </View>
                  {(order.items || []).map((item, itemIndex) => (
                    <View key={itemIndex} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 }}>
                      <Text style={{ width: '55%', fontSize: 8 }}>{item.itemName || `Item ${item.itemCode}`}</Text>
                      <Text style={{ width: '15%', fontSize: 8, textAlign: 'right' }}>x{Number(item.quantity || 0)}</Text>
                      <Text style={{ width: '30%', fontSize: 8, textAlign: 'right' }}>Rs. {Number(item.itemTotal || 0).toFixed(2)}</Text>
                    </View>
                  ))}
                </View>
              ))}
              <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginTop: 3 }}>
                <Text style={{ fontSize: 9, fontWeight: 'bold' }}>Draft Summary: {reportData?.draftSummary?.count || 0} orders</Text>
                <Text style={{ fontSize: 9, fontWeight: 'bold' }}>Rs. {Number(reportData?.draftSummary?.amount || 0).toFixed(2)}</Text>
              </View>
            </View>
          )}

          {reportData?.paymentBreakdown && reportData.paymentBreakdown.length > 0 && (
            <View style={{ marginBottom: 20 }}>
              <Text style={{ fontSize: 11, fontWeight: 'bold', marginBottom: 8 }}>Payment Method Breakdown</Text>
              <View style={{ fontSize: 9, borderBottomWidth: 1, paddingBottom: 5, marginBottom: 5, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <Text style={{ fontWeight: 'bold', width: '40%' }}>Method</Text>
                <Text style={{ fontWeight: 'bold', width: '30%', textAlign: 'right' }}>Count</Text>
                <Text style={{ fontWeight: 'bold', width: '30%', textAlign: 'right' }}>Amount</Text>
              </View>
              {reportData.paymentBreakdown.map((payment, idx) => (
                <View key={idx} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3, fontSize: 9 }}>
                  <Text style={{ width: '40%', textTransform: 'capitalize' }}>{payment.paymentMethod}</Text>
                  <Text style={{ width: '30%', textAlign: 'right' }}>{payment.orderCount}</Text>
                  <Text style={{ width: '30%', textAlign: 'right' }}>Rs. {parseFloat(payment.amount || 0).toFixed(2)}</Text>
                </View>
              ))}
            </View>
          )}

          {reportData?.orderTypeBreakdown && reportData.orderTypeBreakdown.length > 0 && (
            <View style={{ marginBottom: 20 }}>
              <Text style={{ fontSize: 11, fontWeight: 'bold', marginBottom: 8 }}>Sales by Order Type & Payment</Text>
              <View style={{ fontSize: 9, borderBottomWidth: 1, paddingBottom: 5, marginBottom: 5, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <Text style={{ fontWeight: 'bold', width: '25%' }}>Type</Text>
                <Text style={{ fontWeight: 'bold', width: '25%' }}>Payment</Text>
                <Text style={{ fontWeight: 'bold', width: '25%', textAlign: 'right' }}>Count</Text>
                <Text style={{ fontWeight: 'bold', width: '25%', textAlign: 'right' }}>Amount</Text>
              </View>
              {reportData.orderTypeBreakdown.map((item, idx) => (
                <View key={idx} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3, fontSize: 9 }}>
                  <Text style={{ width: '25%', textTransform: 'capitalize' }}>{item.orderType === 'dinein' ? 'Dine In' : item.orderType === 'takeaway' ? 'Takeaway' : item.orderType === 'foodpanda' ? 'Foodpanda' : 'Delivery'}</Text>
                  <Text style={{ width: '25%', textTransform: 'capitalize' }}>{item.paymentMethod}</Text>
                  <Text style={{ width: '25%', textAlign: 'right' }}>{item.orderCount}</Text>
                  <Text style={{ width: '25%', textAlign: 'right' }}>Rs. {parseFloat(item.amount || 0).toFixed(2)}</Text>
                </View>
              ))}
            </View>
          )}

          {reportData?.parkedOrders && reportData.parkedOrders.length > 0 && (
            <View style={{ marginBottom: 20 }}>
              <Text style={{ fontSize: 11, fontWeight: 'bold', marginBottom: 8 }}>Parked Orders</Text>
              <View style={{ fontSize: 9, borderBottomWidth: 1, paddingBottom: 5, marginBottom: 5, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <Text style={{ fontWeight: 'bold', width: '16%' }}>Order ID</Text>
                <Text style={{ fontWeight: 'bold', width: '24%' }}>Type/Table</Text>
                <Text style={{ fontWeight: 'bold', width: '22%' }}>Customer</Text>
                <Text style={{ fontWeight: 'bold', width: '14%', textAlign: 'center' }}>KOT</Text>
                <Text style={{ fontWeight: 'bold', width: '24%', textAlign: 'right' }}>Amount</Text>
              </View>
              {reportData.parkedOrders.map((order, idx) => (
                <View key={idx} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3, fontSize: 9 }}>
                  <Text style={{ width: '16%' }}>{order.orderID}</Text>
                  <Text style={{ width: '24%' }}>{order.orderType || '-'} / {order.tableLabel || '-'}</Text>
                  <Text style={{ width: '22%' }}>{order.customerName || '-'}</Text>
                  <Text style={{ width: '14%', textAlign: 'center', fontWeight: 'bold', color: Number(order.kotPrinted) === 1 ? '#2e7d32' : '#d32f2f' }}>
                    {Number(order.kotPrinted) === 1 ? 'Printed' : 'Not Printed'}
                  </Text>
                  <Text style={{ width: '24%', textAlign: 'right' }}>Rs. {parseFloat(order.grandTotal || 0).toFixed(2)}</Text>
                </View>
              ))}
              <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginTop: 4, borderTopWidth: 1, paddingTop: 4 }}>
                <Text style={{ width: '76%', fontSize: 9, fontWeight: 'bold' }}>
                  Total Parked Orders: {reportData?.parkedSummary?.count || reportData.parkedOrders.length}
                </Text>
                <Text style={{ width: '24%', fontSize: 9, textAlign: 'right', fontWeight: 'bold' }}>
                  Rs. {parseFloat(reportData?.parkedSummary?.amount || 0).toFixed(2)}
                </Text>
              </View>
            </View>
          )}

          {reportVariance && (
            <View style={{ marginTop: 20, borderTopWidth: 1, paddingTop: 10 }}>
              <Text style={{ fontSize: 11, fontWeight: 'bold', marginBottom: 8 }}>Variance Calculation</Text>
              <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text style={{ fontSize: 9 }}>Opening Amount:</Text>
                <Text style={{ fontSize: 9 }}>Rs. {parseFloat(reportVariance.openingAmount || 0).toFixed(2)}</Text>
              </View>
              <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text style={{ fontSize: 9 }}>+ Cash Sales:</Text>
                <Text style={{ fontSize: 9 }}>Rs. {parseFloat(reportVariance.cashSales || 0).toFixed(2)}</Text>
              </View>
              <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4, borderBottomWidth: 1, paddingBottom: 4 }}>
                <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Expected Cash:</Text>
                <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Rs. {parseFloat(reportVariance.expectedAmount || 0).toFixed(2)}</Text>
              </View>
              <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text style={{ fontSize: 9 }}>You Collected:</Text>
                <Text style={{ fontSize: 9 }}>Rs. {parseFloat(reportVariance.collectedAmount || 0).toFixed(2)}</Text>
              </View>
              <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <Text style={{ fontSize: 10, fontWeight: 'bold' }}>{parseFloat(reportVariance.variance || 0) > 0 ? '+ Extra' : '- Short'}:</Text>
                <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Rs. {Math.abs(parseFloat(reportVariance.variance || 0)).toFixed(2)}</Text>
              </View>
            </View>
          )}
        </Page>
      </Document>
    )

    pdf(<ClosingPDF />).toBlob().then((blob) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Closing_${dayjs(reportData?.openingDate).format('YYYY-MM-DD')}_${reportData?.userID || 'User'}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    })
  }


  const handleReprintClosing = async (closingID) => {
    try {
      const response = await axios.post(global.SET_API_URL + 'openingClosing/closing/details', {
        closingID,
        loggedInUserID: user?.userID
      });
      const reportData = response.data?.data;
      if (!reportData) throw new Error('Closing details were not returned');
      const cashSales = (reportData.paymentBreakdown || [])
        .filter(item => String(item.paymentMethod || '').toLowerCase() === 'cash')
        .reduce((sum, item) => sum + Number(item.amount || 0), 0);
      const reportVariance = {
        openingAmount: Number(reportData.openingAmount || 0),
        cashSales,
        expectedAmount: Number(reportData.expectedAmount || 0),
        collectedAmount: Number(reportData.closingAmount || 0),
        variance: Number(reportData.shortExcessAmount || 0),
        variancePercentage: Number(reportData.variancePercentage || 0)
      };
      await handleDownloadPDF(reportData, reportVariance);
    } catch (error) {
      alert('Closing sheet reprint failed: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleViewDraft = async (closingID) => {
    setDraftDialogOpen(true)
    setDraftDialogData(null)
    setDraftLoading(true)
    try {
      const response = await axios.post(global.SET_API_URL + 'openingClosing/closing/details', {
        closingID,
        loggedInUserID: user?.userID
      })
      setDraftDialogData(response.data?.data || null)
    } catch (error) {
      setDraftDialogOpen(false)
      alert('Draft details load failed: ' + (error.response?.data?.message || error.message))
    } finally {
      setDraftLoading(false)
    }
  }

  const renderDraftDialog = () => (
    <Dialog open={draftDialogOpen} onClose={() => setDraftDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        Draft Orders - {draftDialogData?.openingDate ? dayjs(draftDialogData.openingDate).format('DD-MMM-YYYY') : ''}
      </DialogTitle>
      <DialogContent dividers>
        {draftLoading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (draftDialogData?.draftOrders || []).length === 0 ? (
          <Alert severity="info">Is date aur user ke liye koi draft order nahi mila.</Alert>
        ) : (
          <Stack spacing={2}>
            {(draftDialogData?.draftOrders || []).map((order) => (
              <Paper key={order.orderID} variant="outlined" sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: 1.5, bgcolor: '#f3e5f5', display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography sx={{ fontWeight: 800 }}>Order #{order.orderID} - {order.orderType || '-'}</Typography>
                  <Typography sx={{ fontWeight: 800 }}>Rs. {Number(order.grandTotal || 0).toFixed(2)}</Typography>
                </Box>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Item</TableCell><TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Rate</TableCell><TableCell align="right">Amount</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {(order.items || []).map((item) => (
                      <TableRow key={item.orderItemID}>
                        <TableCell>{item.itemName || `Item ${item.itemCode}`}</TableCell>
                        <TableCell align="right">{Number(item.quantity || 0)}</TableCell>
                        <TableCell align="right">Rs. {Number(item.unitPrice || 0).toFixed(2)}</TableCell>
                        <TableCell align="right">Rs. {Number(item.itemTotal || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: '#ede7f6' }}>
              <Typography sx={{ fontWeight: 900 }}>Total Draft Orders: {draftDialogData?.draftSummary?.count || 0}</Typography>
              <Typography sx={{ fontWeight: 900 }}>Rs. {Number(draftDialogData?.draftSummary?.amount || 0).toFixed(2)}</Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={() => setDraftDialogOpen(false)}>Close</Button></DialogActions>
    </Dialog>
  )

  const submitClosing = async () => {
    if (!openingID) {
      alert('No opening found. Please open the till first.')
      return
    }

    if (!varianceData) {
      alert('Please click "Calculate Variance" first')
      return
    }

    dispatch(createClosing({
      openingID: openingID,
      userID: closingUserID,
      loggedInUserID: user?.userID,
      branchID: todayData?.branchID || 1,
      closingAmount: collectedAmount,
      denomination_5000: dailyClosingData.denomination_5000,
      denomination_1000: dailyClosingData.denomination_1000,
      denomination_500: dailyClosingData.denomination_500,
      denomination_100: dailyClosingData.denomination_100,
      denomination_50: dailyClosingData.denomination_50,
      denomination_20: dailyClosingData.denomination_20,
      denomination_10: dailyClosingData.denomination_10,
      denomination_5_coin: dailyClosingData.denomination_5_coin,
      denomination_2_coin: dailyClosingData.denomination_2_coin,
      denomination_1_coin: dailyClosingData.denomination_1_coin,
      remarks: dailyClosingData.remarks,
      applyInventorySnapshot: true
    }))
      .then((result) => {
        if (result.payload?.closingID) {
          // Auto-download PDF before navigating
          handleDownloadPDF()
          
          setTimeout(() => {
            alert('Closing completed successfully!')
            navigate('/DailyOpening')
          }, 1500)
        }
      })
  }

  const renderClosingUserSelector = () => (
    <Grid2 container spacing={2} sx={{ mb: 2 }}>
      <Grid2 size={{ xs: 12, md: 4 }}></Grid2>
      <Grid2 size={{ xs: 12, md: 4 }}>
        <Autocomplete
          options={closingUserOptions}
          getOptionLabel={(option) => option?.userName
            ? `${option.userName} (${option.dispName || option.userName})`
            : (option?.dispName || '')}
          value={selectedClosingUser}
          onChange={(event, newValue) => {
            if (newValue && newValue.userID) {
                      setClosingUserID(newValue.userID)
              setShowVariance(false)
              setVarianceData(null)
            }
          }}
          renderInput={(params) => <TextField {...params} label='Select User for Closing' size='small' />}
          size='small'
          isOptionEqualToValue={(option, value) => Number(option?.userID) === Number(value?.userID)}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 4 }}></Grid2>
    </Grid2>
  )
  if (loading && !todayData) {
    return (
      <>
        <Navbar />
        <Container sx={{ textAlign: 'center', mt: 5 }}>
          <Loading loadingText='Loading Opening Data' />
        </Container>
      </>
    )
  }

  if (!todayData || !todayData.openingID) {
    return (
      <>
        <Navbar />
        <Container sx={{ mt: 5 }}>
          {renderClosingUserSelector()}
          <Alert severity="warning">
            <Typography variant='h6'>No opening found for today.</Typography>
            <Typography variant='body1'>Please open the till first by going to Daily Opening screen.</Typography>
          </Alert>
          <Box sx={{ mt: 2 }}>
            <ButtonNormal text='Go to Opening' onClick={() => navigate('/DailyOpening')} />

            {history && history.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant='h6' sx={{ mb: 2, fontWeight: 'bold' }}>Recent Closing History</Typography>
                <TableContainer component={Paper}>
                  <Table size='small'>
                    <TableHead><TableRow>
                      <TableCell>Date</TableCell><TableCell>Till</TableCell>
                      <TableCell align="right">Closing</TableCell><TableCell align="center">Actions</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {history.map((item) => (
                        <TableRow key={item.closingID}>
                          <TableCell>{dayjs(item.closingDate).format('DD-MMM-YYYY')}</TableCell>
                          <TableCell>{item.tillID || '-'}</TableCell>
                          <TableCell align="right">Rs. {parseFloat(item.closingAmount || 0).toFixed(2)}</TableCell>
                          <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ minWidth: 72 }}>
                          <Tooltip title="Reprint Closing">
                            <IconButton size="small" onClick={() => handleReprintClosing(item.closingID)} sx={{ color: '#b71c1c', border: '1px solid #ef9a9a', p: 0.65 }}>
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Draft">
                            <IconButton size="small" onClick={() => handleViewDraft(item.closingID)} sx={{ color: '#6a1b9a', border: '1px solid #b39ddb', p: 0.65 }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </Container>
        {renderDraftDialog()}
      </>
    )
  }

  return (
    <>
      <Navbar />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {renderClosingUserSelector()}

        <HeadingText text='Daily Closing' />

        {/* Summary Box */}
        <Box sx={{
          mt: 3,
          p: 2,
          backgroundColor: '#e3f2fd',
          borderRadius: 2,
          border: '1px solid #90caf9'
        }}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <Typography variant='body2' color='textSecondary'>Opening Date</Typography>
              <Typography variant='h6'>{dayjs(todayData?.openingDate).format('DD-MMM-YYYY')}</Typography>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <Typography variant='body2' color='textSecondary'>Opening Time</Typography>
              <Typography variant='h6'>{todayData?.openingTime || 'N/A'}</Typography>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <Typography variant='body2' color='textSecondary'>Opening Amount</Typography>
              <Typography variant='h6'>Rs. {todayData?.openingAmount}</Typography>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <Typography variant='body2' color='textSecondary'>Sales (Since Opening)</Typography>
              <Typography variant='h6' sx={{ color: '#d32f2f' }}>Rs. {todayData?.salesAmount}</Typography>
            </Grid2>
            <Grid2 size={{ xs: 12 }} sx={{ backgroundColor: '#fff3e0', p: 1.5, borderRadius: 1 }}>
              <Typography variant='body2' sx={{ fontSize: '0.9rem', color: '#e65100' }}>
                Cancelled Orders Summary: <strong>{todayData?.cancelledSummary?.count || 0}</strong> orders
                {' | '}Amount: <strong>Rs. {parseFloat(todayData?.cancelledSummary?.amount || 0).toFixed(2)}</strong>
              </Typography>
            </Grid2>
            <Grid2 size={{ xs: 12 }} sx={{ backgroundColor: '#f3e5f5', p: 1.5, borderRadius: 1 }}>
              <Typography variant='body2' sx={{ fontSize: '0.9rem', color: '#6a1b9a' }}>
                Draft Orders Summary (Not Included in Sales/Expected):
                {' '}<strong>{todayData?.draftSummary?.count || 0}</strong> orders
                {' | '}Amount: <strong>Rs. {parseFloat(todayData?.draftSummary?.amount || 0).toFixed(2)}</strong>
              </Typography>
            </Grid2>
            <Grid2 size={{ xs: 12 }} sx={{ borderTop: '1px solid #90caf9', pt: 2 }}>
              <Typography variant='body2' color='textSecondary'>Expected Amount (Opening + Sales)</Typography>
              <Typography variant='h5' sx={{ fontWeight: 'bold', color: '#1976d2' }}>Rs. {todayData?.expectedAmount}</Typography>
            </Grid2>
            <Grid2 size={{ xs: 12 }} sx={{ backgroundColor: '#f5f5f5', p: 1, borderRadius: 1 }}>
              <Typography variant='body2' sx={{ fontSize: '0.85rem', color: '#666' }}>
                📌 یاد رکھیں: یہ sales اس وقت سے شمار ہیں جب opening ہوئی تھی ({dayjs(todayData?.openingDate).format('DD-MMM')} {todayData?.openingTime})
                <br/>ممکن ہے closing اگلے دن ہو (جیسے subha 3 AM)
              </Typography>
            </Grid2>
          </Grid2>
        </Box>

        {estimatedStock && (
          <Box sx={{ mt: 3 }}>
            <Typography variant='h6' sx={{ mb: 1, fontWeight: 'bold', color: '#2e7d32' }}>
              Estimated Inventory (Temporary View)
            </Typography>
            <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: '#e8f5e9', borderRadius: 1, border: '1px solid #a5d6a7' }}>
              <Typography variant='body2' sx={{ fontSize: '0.88rem', color: '#1b5e20' }}>
                Previous Closing Baseline: <strong>{estimatedStock?.summary?.baselineSource || 'N/A'}</strong>
                {' | '}Items: <strong>{estimatedStock?.summary?.totalItems || 0}</strong>
                {' | '}Negative Stock Alerts: <strong>{estimatedStock?.summary?.negativeStockItems || 0}</strong>
              </Typography>
              <Typography variant='body2' sx={{ fontSize: '0.82rem', color: '#33691e', mt: 0.5 }}>
                This is preview only (unposted). Final inventory posting will happen at Close Till.
              </Typography>
            </Box>
            <TableContainer component={Paper} sx={{ backgroundColor: '#f8fff8' }}>
              <Table size='small'>
                <TableHead sx={{ backgroundColor: '#e8f5e9' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Item Code</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 'bold' }}>Previous Closing</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 'bold' }}>Today Consumed</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 'bold' }}>Estimated Current</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(estimatedStock?.rows || []).slice(0, 100).map((row) => (
                    <TableRow
                      key={row.itemCode}
                      sx={{
                        '&:hover': { backgroundColor: '#f1f8e9' },
                        backgroundColor: row.estimatedStockQty < 0 ? '#fff3e0' : 'transparent'
                      }}
                    >
                      <TableCell>{row.itemCode}</TableCell>
                      <TableCell>{row.itemDesc}</TableCell>
                      <TableCell align='right'>{Number(row.previousClosingStock || 0).toFixed(3)}</TableCell>
                      <TableCell align='right'>{Number(row.todayConsumedQty || 0).toFixed(3)}</TableCell>
                      <TableCell
                        align='right'
                        sx={{
                          fontWeight: 700,
                          color: Number(row.estimatedStockQty || 0) < 0 ? '#d84315' : '#1b5e20'
                        }}
                      >
                        {Number(row.estimatedStockQty || 0).toFixed(3)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {(estimatedStock?.rows || []).length > 100 && (
              <Typography variant='body2' sx={{ mt: 1, fontSize: '0.8rem', color: '#5d4037' }}>
                Showing first 100 items out of {(estimatedStock?.rows || []).length}. Full posting happens on closing.
              </Typography>
            )}
          </Box>
        )}

        {history && history.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant='h6' sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
              Recent Closing History
            </Typography>
            <TableContainer component={Paper} sx={{ backgroundColor: '#f9f9f9', overflowX: 'auto' }}>
              <Table size='small' sx={{ minWidth: 1050, '& th, & td': { whiteSpace: 'nowrap', px: 1, py: 1 } }}>
                <TableHead sx={{ backgroundColor: '#e3f2fd' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Till ID</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Opening</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Sales</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Expected</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Closing</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Variance %</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Draft Orders</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Draft Amount</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((item, index) => (
                    <TableRow key={item.closingID || index} sx={{ '&:hover': { backgroundColor: '#f0f0f0' } }}>
                      <TableCell>{dayjs(item.closingDate).format('DD-MMM-YYYY')}</TableCell>
                      <TableCell>{item.tillID || '-'}</TableCell>
                      <TableCell align="right">Rs. {parseFloat(item.openingAmount || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">Rs. {parseFloat(item.salesAmount || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">Rs. {parseFloat(item.expectedAmount || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">Rs. {parseFloat(item.closingAmount || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">{parseFloat(item.variancePercentage || 0).toFixed(2)}%</TableCell>
                      <TableCell align="right">{Number(item.draftCount || 0)}</TableCell>
                      <TableCell align="right" sx={{ color: '#6a1b9a', fontWeight: 600 }}>
                        Rs. {parseFloat(item.draftAmount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ minWidth: 72 }}>
                          <Tooltip title="Reprint Closing">
                            <IconButton size="small" onClick={() => handleReprintClosing(item.closingID)} sx={{ color: '#b71c1c', border: '1px solid #ef9a9a', p: 0.65 }}>
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Draft">
                            <IconButton size="small" onClick={() => handleViewDraft(item.closingID)} sx={{ color: '#6a1b9a', border: '1px solid #b39ddb', p: 0.65 }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 1, p: 1, backgroundColor: '#f3e5f5', borderRadius: 1 }}>
              <Typography variant='body2' sx={{ fontSize: '0.82rem', color: '#6a1b9a' }}>
                Draft columns are summary-only and not included in Sales / Expected / Variance calculations.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Payment Method Breakdown */}
        {todayData?.paymentBreakdown && todayData.paymentBreakdown.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant='h6' sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>Payment Method Breakdown</Typography>
            <TableContainer component={Paper} sx={{ backgroundColor: '#f9f9f9', overflowX: 'auto' }}>
              <Table size='small' sx={{ minWidth: 1050, '& th, & td': { whiteSpace: 'nowrap', px: 1, py: 1 } }}>
                <TableHead sx={{ backgroundColor: '#e3f2fd' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Payment Method</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Order Count</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount (Rs.)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {todayData.paymentBreakdown.map((payment, index) => (
                    <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f0f0f0' } }}>
                      <TableCell>
                        <Typography variant='body2' sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                          {payment.paymentMethod}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{payment.orderCount}</TableCell>
                      <TableCell 
                        align="right"
                        sx={{ 
                          fontWeight: 'bold',
                          color: payment.paymentMethod === 'cash' ? '#d32f2f' : '#1976d2'
                        }}
                      >
                        Rs. {parseFloat(payment.amount).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#fffde7', borderRadius: 1 }}>
              <Typography variant='body2' sx={{ fontSize: '0.85rem', color: '#f57f17' }}>
                💡 <strong>Tip:</strong> {todayData.paymentBreakdown.find(p => p.paymentMethod === 'cash')?.amount ? `Expected Cash in Till = Rs. ${(parseFloat(todayData.openingAmount) + parseFloat(todayData.paymentBreakdown.find(p => p.paymentMethod === 'cash')?.amount || 0)).toFixed(2)}` : 'No cash payments recorded'}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Order Type & Payment Method Breakdown */}
        {todayData?.orderTypeBreakdown && todayData.orderTypeBreakdown.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant='h6' sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>Sales by Order Type & Payment Method</Typography>
            <TableContainer component={Paper} sx={{ backgroundColor: '#f9f9f9', overflowX: 'auto' }}>
              <Table size='small' sx={{ minWidth: 1050, '& th, & td': { whiteSpace: 'nowrap', px: 1, py: 1 } }}>
                <TableHead sx={{ backgroundColor: '#e3f2fd' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Order Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Payment Method</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Order Count</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount (Rs.)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {todayData.orderTypeBreakdown.map((item, index) => (
                    <TableRow 
                      key={index} 
                      sx={{ 
                        '&:hover': { backgroundColor: '#f0f0f0' },
                        backgroundColor: item.orderType === 'foodpanda' ? '#fff3e0' : 'transparent'
                      }}
                    >
                      <TableCell>
                        <Typography variant='body2' sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
                          {item.orderType?.replace('dinein', 'Dine In').replace('takeaway', 'Takeaway').replace('delivery', 'Delivery').replace('foodpanda', 'Foodpanda') || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2' sx={{ textTransform: 'capitalize' }}>
                          {item.paymentMethod}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{item.orderCount}</TableCell>
                      <TableCell 
                        align="right"
                        sx={{ 
                          fontWeight: 'bold',
                          color: item.paymentMethod === 'cash' ? '#d32f2f' : item.orderType === 'foodpanda' ? '#f57f17' : '#1976d2'
                        }}
                      >
                        Rs. {parseFloat(item.amount).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Important Notes for Pending Payments */}
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ffb74d' }}>
              <Typography variant='body2' sx={{ fontSize: '0.85rem', color: '#e65100', fontWeight: 600, mb: 1 }}>
                ⚠️ Payment Pending Reminders:
              </Typography>
              {todayData.orderTypeBreakdown.find(item => item.orderType === 'delivery' && item.paymentMethod !== 'cash') && (
                <Typography variant='body2' sx={{ fontSize: '0.85rem', color: '#e65100', mb: 1 }}>
                  • Delivery (Card/Other): Check if delivery partner payment is pending
                </Typography>
              )}
              {todayData.orderTypeBreakdown.find(item => item.orderType === 'foodpanda') && (
                <Typography variant='body2' sx={{ fontSize: '0.85rem', color: '#e65100', mb: 1 }}>
                  • Foodpanda: <strong>Weekly Payment</strong> - Payment usually received on {new Date(new Date(todayData?.openingDate).getTime() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB')}
                </Typography>
              )}
              <Typography variant='body2' sx={{ fontSize: '0.85rem', color: '#e65100' }}>
                📌 Compare cash collected with expected cash from [Dine In + Takeaway] + [Delivery Cash]
              </Typography>
            </Box>
          </Box>
        )}

        {todayData?.parkedOrders && todayData.parkedOrders.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant='h6' sx={{ mb: 2, fontWeight: 'bold', color: '#d32f2f' }}>Parked Orders</Typography>
            <TableContainer component={Paper} sx={{ backgroundColor: '#fff8f8' }}>
              <Table size='small'>
                <TableHead sx={{ backgroundColor: '#ffebee' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Order ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Order Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Table</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                    <TableCell align='center' sx={{ fontWeight: 'bold' }}>KOT Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount (Rs.)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {todayData.parkedOrders.map((order, index) => (
                    <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#ffebee' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>#{order.orderID}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{order.orderType || '-'}</TableCell>
                      <TableCell>{order.tableLabel || '-'}</TableCell>
                      <TableCell>{order.customerName || '-'}</TableCell>
                      <TableCell align='center'>
                        <Chip
                          label={Number(order.kotPrinted) === 1 ? 'KOT Printed' : 'KOT Not Printed'}
                          color={Number(order.kotPrinted) === 1 ? 'success' : 'error'}
                          size='small'
                          variant='outlined'
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                        Rs. {parseFloat(order.grandTotal || 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: '#ffcdd2' }}>
                    <TableCell colSpan={5} sx={{ fontWeight: 'bold' }}>
                      Total Parked Orders: {todayData?.parkedSummary?.count || todayData.parkedOrders.length}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#b71c1c' }}>
                      Rs. {parseFloat(todayData?.parkedSummary?.amount || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {todayData?.cancelledOrders && todayData.cancelledOrders.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant='h6' sx={{ mb: 2, fontWeight: 'bold', color: '#ef6c00' }}>Cancelled Orders</Typography>
            <TableContainer component={Paper} sx={{ backgroundColor: '#fffaf2' }}>
              <Table size='small'>
                <TableHead sx={{ backgroundColor: '#ffe0b2' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Order ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Order Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Table</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount (Rs.)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {todayData.cancelledOrders.map((order, index) => (
                    <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#fff3e0' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>#{order.orderID}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{order.orderType || '-'}</TableCell>
                      <TableCell>{order.tableLabel || '-'}</TableCell>
                      <TableCell>{order.customerName || '-'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ef6c00' }}>
                        Rs. {parseFloat(order.grandTotal || 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: '#ffe0b2' }}>
                    <TableCell colSpan={4} sx={{ fontWeight: 'bold' }}>
                      Total Cancelled Orders: {todayData?.cancelledSummary?.count || todayData.cancelledOrders.length}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#e65100' }}>
                      Rs. {parseFloat(todayData?.cancelledSummary?.amount || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Variance Alert */}
        {showVariance && varianceData && (
          <Box sx={{ mt: 2 }}>
            <Alert severity={varianceData.isExcess ? 'success' : varianceData.isShort ? 'error' : 'info'}>
              <Box sx={{ mb: 2 }}>
                <Typography variant='h6' sx={{ mb: 1 }}>
                  {varianceData.isExcess ? '✓ Excess' : varianceData.isShort ? '✗ Short' : '= Balanced'}
                  {' | Variance: Rs. '} {Math.abs(varianceData.variance).toFixed(2)}
                  {' ('} {varianceData.variancePercentage}% {')'}
                </Typography>
              </Box>
              
              <Box sx={{ backgroundColor: 'rgba(255,255,255,0.1)', p: 1.5, borderRadius: 1, mb: 1.5 }}>
                <Typography variant='body2' sx={{ mb: 1, fontSize: '0.9rem' }}>
                  <strong>Calculation:</strong>
                </Typography>
                <Typography variant='body2' sx={{ mb: 0.5, fontSize: '0.9rem' }}>
                  Opening Amount: <strong>Rs. {varianceData.openingAmount.toFixed(2)}</strong>
                </Typography>
                <Typography variant='body2' sx={{ mb: 0.5, fontSize: '0.9rem' }}>
                  + Cash Sales (all types): <strong>Rs. {varianceData.cashSales.toFixed(2)}</strong>
                </Typography>
                <Typography variant='body2' sx={{ mb: 1, fontSize: '0.9rem', borderTop: '1px solid rgba(255,255,255,0.3)', pt: 1 }}>
                  = Expected Cash in Till: <strong style={{ fontSize: '1.1rem', color: '#fff' }}>Rs. {varianceData.expectedAmount.toFixed(2)}</strong>
                </Typography>
              </Box>

              <Box sx={{ backgroundColor: 'rgba(255,255,255,0.1)', p: 1.5, borderRadius: 1 }}>
                <Typography variant='body2' sx={{ mb: 0.5, fontSize: '0.9rem' }}>
                  You Collected: <strong>Rs. {varianceData.collectedAmount.toFixed(2)}</strong>
                </Typography>
                <Typography variant='body2' sx={{ fontSize: '0.9rem', color: varianceData.variance > 0 ? '#4caf50' : '#f44336' }}>
                  {varianceData.variance > 0 ? '+ Extra' : '- Short'}: <strong>Rs. {Math.abs(varianceData.variance).toFixed(2)}</strong>
                </Typography>
              </Box>
            </Alert>
          </Box>
        )}

        {/* Error Messages */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error?.message || 'Error creating closing'}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Closing created successfully!
          </Alert>
        )}

        {/* Denomination Input Section */}
        <Box sx={{ mt: 3 }}>
          <Typography variant='h6' sx={{ mb: 2, fontWeight: 'bold' }}>Enter Collected Denominations</Typography>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 6, md: 1.4 }}>
              <TextField
                id="denomination_5000"
                label="5000"
                variant="outlined"
                fullWidth
                name="denomination_5000"
                onChange={handleChange}
                size='small'
                value={dailyClosingData.denomination_5000}
                type="number"
              />
            </Grid2>
            <Grid2 size={{ xs: 6, md: 1.4 }}>
              <TextField
                id="denomination_1000"
                label="1000"
                variant="outlined"
                fullWidth
                name="denomination_1000"
                onChange={handleChange}
                size='small'
                value={dailyClosingData.denomination_1000}
                type="number"
              />
            </Grid2>
            <Grid2 size={{ xs: 6, md: 1.4 }}>
              <TextField
                id="denomination_500"
                label="500"
                variant="outlined"
                fullWidth
                name="denomination_500"
                onChange={handleChange}
                size='small'
                value={dailyClosingData.denomination_500}
                type="number"
              />
            </Grid2>
            <Grid2 size={{ xs: 6, md: 1.4 }}>
              <TextField
                id="denomination_100"
                label="100"
                variant="outlined"
                fullWidth
                name="denomination_100"
                onChange={handleChange}
                size='small'
                value={dailyClosingData.denomination_100}
                type="number"
              />
            </Grid2>
            <Grid2 size={{ xs: 6, md: 1.4 }}>
              <TextField
                id="denomination_50"
                label="50"
                variant="outlined"
                fullWidth
                name="denomination_50"
                onChange={handleChange}
                size='small'
                value={dailyClosingData.denomination_50}
                type="number"
              />
            </Grid2>
            <Grid2 size={{ xs: 6, md: 1.4 }}>
              <TextField
                id="denomination_20"
                label="20"
                variant="outlined"
                fullWidth
                name="denomination_20"
                onChange={handleChange}
                size='small'
                value={dailyClosingData.denomination_20}
                type="number"
              />
            </Grid2>
            <Grid2 size={{ xs: 6, md: 1.4 }}>
              <TextField
                id="denomination_10"
                label="10"
                variant="outlined"
                fullWidth
                name="denomination_10"
                onChange={handleChange}
                size='small'
                value={dailyClosingData.denomination_10}
                type="number"
              />
            </Grid2>
            <Grid2 size={{ xs: 6, md: 1.4 }}>
              <TextField
                id="denomination_5_coin"
                label="5 (Coin)"
                variant="outlined"
                fullWidth
                name="denomination_5_coin"
                onChange={handleChange}
                size='small'
                value={dailyClosingData.denomination_5_coin}
                type="number"
              />
            </Grid2>
            <Grid2 size={{ xs: 6, md: 1.4 }}>
              <TextField
                id="denomination_2_coin"
                label="2 (Coin)"
                variant="outlined"
                fullWidth
                name="denomination_2_coin"
                onChange={handleChange}
                size='small'
                value={dailyClosingData.denomination_2_coin}
                type="number"
              />
            </Grid2>
            <Grid2 size={{ xs: 6, md: 1.4 }}>
              <TextField
                id="denomination_1_coin"
                label="1 (Coin)"
                variant="outlined"
                fullWidth
                name="denomination_1_coin"
                onChange={handleChange}
                size='small'
                value={dailyClosingData.denomination_1_coin}
                type="number"
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 1.6 }}>
              <TextField
                id="collectedAmount"
                label="Collected Amount"
                variant="outlined"
                fullWidth
                name="collectedAmount"
                disabled
                size='small'
                value={collectedAmount}
                type="number"
              />
            </Grid2>
          </Grid2>

          {/* Remarks Field */}
          <Grid2 container spacing={2} sx={{ mt: 1 }}>
            <Grid2 size={{ xs: 12 }}>
              <TextField
                id="remarks"
                label="Remarks (Optional)"
                variant="outlined"
                fullWidth
                name="remarks"
                onChange={handleChange}
                size='small'
                value={dailyClosingData.remarks}
                multiline
                rows={2}
              />
            </Grid2>
          </Grid2>

          {/* Action Buttons */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <ButtonNormal
              text='Calculate Variance'
              onClick={calculateVariance}
              disabled={loading}
            />
            <ButtonNormal
              text='Close Till'
              onClick={submitClosing}
              disabled={loading || !showVariance}
            />
          </Box>

          {/* Print & Download Buttons */}
          {showVariance && varianceData && (
            <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Box
                component="button"
                onClick={handlePrint}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 16px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  },
                  '@media print': {
                    display: 'none'
                  }
                }}
              >
                <PrintIcon sx={{ fontSize: '1.2rem' }} />
                Print
              </Box>
              <Box
                component="button"
                onClick={handleDownloadPDF}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 16px',
                  backgroundColor: '#388e3c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#2e7d32',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  }
                }}
              >
                <DownloadIcon sx={{ fontSize: '1.2rem' }} />
                Download PDF
              </Box>
            </Box>
          )}
        </Box>
      </Container>
      {renderDraftDialog()}
    </>
  )
}
